import React from "react";
import { Mail, Phone, MapPin, Clock, ArrowUpRight } from "lucide-react";

const Contact = () => {
    return (
        <section
            id="contact"
            data-testid="contact-section"
            className="py-24 md:py-36 bg-sun-bg relative"
        >
            <div className="container-x">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-14">
                    <div className="col-span-12 md:col-span-7">
                        <div className="overline mb-4">— Visit us</div>
                        <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em] text-sun-ink">
                            Come say
                            <br />
                            <span className="display-italic text-gradient">hello.</span>
                        </h2>
                        <p className="mt-6 text-sun-inkSoft text-base md:text-lg leading-relaxed max-w-xl">
                            Our Surat facility is open Monday through Saturday. Walk-ins welcome
                            — call ahead if you'd like us to prepare samples of a specific SKU.
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-5 flex md:justify-end items-end">
                        <a
                            href="https://wa.me/919737028999?text=Hi%20Ostrich%20Foods%2C%20I%27d%20like%20to%20know%20more%20about%20your%20powders."
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="contact-whatsapp-cta"
                            className="btn-gradient"
                        >
                            Chat on WhatsApp
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>

                {/* Map + address card */}
                <div className="grid grid-cols-12 gap-4 md:gap-6">
                    {/* Address panel */}
                    <div className="col-span-12 md:col-span-5 bg-sun-ink text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
                        <div className="absolute -top-32 -right-32 w-80 h-80 sun-rays opacity-15 animate-spin-slow pointer-events-none" />
                        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-sunrise opacity-20 blur-3xl pointer-events-none" />

                        <div className="relative space-y-7">
                            <Row
                                icon={Phone}
                                label="Phone"
                                value="+91 9737 028 999"
                                href="tel:+919737028999"
                            />
                            <Row
                                icon={Mail}
                                label="Email"
                                value="trade@ostrichfoods.in"
                                href="mailto:trade@ostrichfoods.in"
                            />
                            <Row
                                icon={MapPin}
                                label="Facility"
                                value="Surat, Gujarat — India"
                                href="https://maps.app.goo.gl/kX8rZgi4fePP4NtD9"
                            />
                            <Row
                                icon={Clock}
                                label="Hours"
                                value="Mon – Sat · 10:00 – 18:00 IST"
                            />
                        </div>
                    </div>

                    {/* Map */}
                    <div className="col-span-12 md:col-span-7">
                        <div className="flex items-center justify-between mb-3">
                            <div className="overline">— Find us on the map</div>
                            <a
                                href="https://maps.app.goo.gl/kX8rZgi4fePP4NtD9?g_st=ic"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-open-gmaps"
                                className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-orange hover:text-sun-ink transition-colors"
                            >
                                Open in Google Maps ↗
                            </a>
                        </div>
                        <div
                            data-testid="contact-map"
                            className="rounded-3xl overflow-hidden border border-sun-line bg-white aspect-[4/3] md:aspect-auto md:h-full min-h-[360px] shadow-sm"
                        >
                            <iframe
                                title="Ostrich Foods · Surat, Gujarat"
                                src="https://www.google.com/maps?q=21.0968701,72.8939069&z=16&output=embed"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Row = ({ icon: Icon, label, value, href }) => {
    const inner = (
        <div className="flex gap-4 items-start group">
            <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/15 text-sun-gold flex items-center justify-center flex-shrink-0 group-hover:bg-sun-orange group-hover:text-white group-hover:border-sun-orange transition-colors">
                <Icon className="w-5 h-5" strokeWidth={2} />
            </div>
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-sun-goldSoft">
                    {label}
                </div>
                <div className="mt-1 font-heading text-xl md:text-2xl leading-tight text-white group-hover:text-sun-gold transition-colors">
                    {value}
                </div>
            </div>
        </div>
    );
    return href ? (
        <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
            {inner}
        </a>
    ) : inner;
};

export default Contact;
